package org.example.project.game

import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.database.database
import kotlinx.coroutines.*
import kotlinx.coroutines.flow.*

/**
 * MultiplayerLobbyViewModel — Raum erstellen oder beitreten.
 *
 * Schreibt das exakte App-Format (siehe MainActivity.createRoom).
 * Bei Host: erzeugt 6-stelligen Code und schreibt initialen Raum.
 * Bei Gast: liest Raum, prueft Status/Spieler-Anzahl, joined als
 *           Spieler (oder Spectator wenn schon 4 aktive drin).
 */
class MultiplayerLobbyViewModel(
    private val playerName: String,
    private val playerAvatar: String,   // String wie "avatargreen"
    private val langCode: String,
    private val difficulty: String
) {
    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Main)
    private val db = Firebase.database("https://assoziation-c5cda-default-rtdb.firebaseio.com")
    private val roomsRef = db.reference("rooms")
    private val levelLoader = LevelLoader()

    private val playerId: String = getOrCreatePlayerId()

    private val _isLoading = MutableStateFlow(false)
    val isLoading = _isLoading.asStateFlow()

    private val _errorMessage = MutableStateFlow<String?>(null)
    val errorMessage = _errorMessage.asStateFlow()

    private val _currentRoomCode = MutableStateFlow<String?>(null)
    val currentRoomCode = _currentRoomCode.asStateFlow()

    private val _navigateToGame = MutableStateFlow(false)
    val navigateToGame = _navigateToGame.asStateFlow()

    private var roomListenerJob: Job? = null

    // Wird vom Lobby/GameScreen vor Navigation gesetzt
    private var gameStarted = false

    fun createRoom() {
        scope.launch {
            _isLoading.value = true
            _errorMessage.value = null
            try {
                val roomCode = (100000..999999).random().toString()

                // Level via LevelLoader holen
                val pair = levelLoader.fetchRandomLevel(langCode, difficulty)
                if (pair == null) {
                    _errorMessage.value = "Kein Level gefunden"
                    _isLoading.value = false
                    return@launch
                }
                val (gameId, _) = pair

                val newRoom = GameRoom(
                    roomCode = roomCode,
                    gameId = gameId,
                    status = "waiting",
                    lang = langCode,
                    difficulty = difficulty,
                    players = mapOf(playerId to true),
                    playerInfo = mapOf(
                        playerId to PlayerInfo(name = playerName, avatar = playerAvatar)
                    ),
                    scores = mapOf(playerId to 0),
                    whosTurn = playerId,
                    hintUsed = false,
                    jokerUsed = false,
                    answerUsed = false,
                    revealedHints = emptyMap(),
                    solvedColumns = emptyMap(),
                    giveUpVotes = emptyMap(),
                    spectators = emptyMap(),
                    winner = ""
                )

                roomsRef.child(roomCode).setValue(GameRoom.serializer(), newRoom)

                _currentRoomCode.value = roomCode
                _isLoading.value = false

                listenForGuestJoin(roomCode)
            } catch (e: Exception) {
                _errorMessage.value = "Fehler beim Erstellen: ${e.message}"
                _isLoading.value = false
            }
        }
    }

    fun joinRoom(roomCode: String) {
        if (roomCode.length != 6) {
            _errorMessage.value = "Code muss 6-stellig sein"
            return
        }
        scope.launch {
            _isLoading.value = true
            _errorMessage.value = null
            try {
                withTimeout(5000) {
                    val snapshot = roomsRef.child(roomCode).valueEvents.first()
                    if (!snapshot.exists) {
                        _errorMessage.value = "Raum nicht gefunden"
                        _isLoading.value = false
                        return@withTimeout
                    }
                    val room = snapshot.value(GameRoom.serializer())

                    // Spiel laeuft bereits? (Hinweise / Spalten geloest / Gewinner)
                    val running = room.revealedHints.isNotEmpty() ||
                                  room.solvedColumns.isNotEmpty() ||
                                  room.winner.isNotEmpty()
                    if (running) {
                        _errorMessage.value = "Spiel laeuft bereits"
                        _isLoading.value = false
                        return@withTimeout
                    }

                    val activeCount = room.players.size
                    val updates = mutableMapOf<String, Any?>()

                    if (activeCount >= 4) {
                        // Als Spectator joinen
                        updates["spectators/$playerId"] = true
                    } else {
                        updates["players/$playerId"] = true
                        updates["scores/$playerId"] = 0
                    }
                    updates["playerInfo/$playerId/name"] = playerName
                    updates["playerInfo/$playerId/avatar"] = playerAvatar

                    // Wenn 2+ aktive Spieler, status auf "playing"
                    val newActive = if (activeCount >= 4) activeCount else activeCount + 1
                    if (newActive >= 2) updates["status"] = "playing"

                    @Suppress("UNCHECKED_CAST")
                    roomsRef.child(roomCode).updateChildren(updates as Map<String, Any>)

                    _currentRoomCode.value = roomCode
                    _navigateToGame.value = true
                    _isLoading.value = false
                }
            } catch (e: Exception) {
                _errorMessage.value = "Verbindungsfehler oder falscher Code"
                _isLoading.value = false
            }
        }
    }

    private fun listenForGuestJoin(roomCode: String) {
        roomListenerJob?.cancel()
        roomListenerJob = scope.launch {
            try {
                roomsRef.child(roomCode).valueEvents.collect { snapshot ->
                    if (!snapshot.exists) return@collect
                    val room = snapshot.value(GameRoom.serializer())
                    // Sobald Status "playing" oder mind. 2 Spieler -> Navigation
                    if (room.status == "playing" || room.players.size >= 2) {
                        _navigateToGame.value = true
                    }
                }
            } catch (_: Exception) {}
        }
    }

    fun onGameStarted() {
        gameStarted = true
    }

    fun cancelRoom() {
        _currentRoomCode.value?.let { code ->
            scope.launch { try { roomsRef.child(code).removeValue() } catch (_: Exception) {} }
        }
        _currentRoomCode.value = null
        roomListenerJob?.cancel()
    }

    fun dispose() {
        if (!gameStarted) {
            cancelRoom()
        } else {
            roomListenerJob?.cancel()
        }
        scope.cancel()
    }
}